import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { getInstanceSettings } from '../../app/selectors'
import { DashboardVisualization, DashboardVisualizationProps } from '../components/DashboardVisualization'
import * as dashboardSelectors from '../selectors'
import { actions } from '../actions'

export interface StateToProps {
    dashboardSettings?: DashboardSettingsDto
    controlId: number
    showModule: boolean
    titleStyle: CSSProperties
    subTitleStyle: CSSProperties
    showTitle: boolean
    showSubTitle: boolean
    options: any
    campaignsStyle: CSSProperties
    dollarsStyle: CSSProperties
    contributionsStyle: CSSProperties    
    donorAmount: number
    dollarAmount: number
}

export interface MergeProps {
    onEvents: { legendselectchanged: (props: { selected: LegendItems }) => void }
    options: any
}

export const mapStateToProps = (state: RootState & { browser: { width: number } }): StateToProps => {
    const dashboardSettings = getInstanceSettings(state) as DashboardSettingsDto | undefined
    const titleStyle = dashboardSelectors.getTitleTextStyle(state) as React.CSSProperties
    const subTitleStyle = dashboardSelectors.getSubTitleTextStyle(state) as React.CSSProperties
    const showModule = imod.cms.scoreboards.overrideDisplayDate || dashboardSelectors.getShowModule(state)
    const campaignsStyle = dashboardSelectors.getCampaignTextStyle(state) as React.CSSProperties
    const dollarsStyle = dashboardSelectors.getDollarsTextStyle(state) as React.CSSProperties
    const contributionsStyle = dashboardSelectors.getContributionsTextStyle(state) as React.CSSProperties

    const options = dashboardSelectors.getGraphType(state) === 'Donors' ? dashboardSelectors.getDonorGraphOptions(state) : dashboardSelectors.getDollarGraphOptions(state)
    return {
        options,
        controlId: state.config.controlId,
        dashboardSettings,
        donorAmount: dashboardSelectors.getTotalDonors(state) || 0,
        dollarAmount: dashboardSelectors.getTotalDollars(state) || 0,
        titleStyle,
        subTitleStyle,
        showModule,
        campaignsStyle,
        contributionsStyle,
        dollarsStyle,
        showTitle: Boolean(titleStyle && showModule),
        showSubTitle: Boolean(dashboardSettings && dashboardSettings.moduleSubTitle && subTitleStyle && showModule),        
    }
}

export const mergeProps = (stateToProps: StateToProps, { dispatch }: { dispatch: Dispatch }, ownProps: DashboardVisualizationProps): MergeProps & DashboardVisualizationProps => ({
    ...stateToProps,
    ...ownProps,
    onEvents: {
        legendselectchanged: ({ selected }: { selected: LegendItems }) => dispatch(actions.updateLegend(selected)),
    },
    options: {
        ...stateToProps.options,
        animation: false,
        toolbox: {
            width: 100,
            orient: 'vertical',
            itemGap: 16,
            bottom: 25,
            itemSize: 20,
            right: 2,
            feature: {
                myLine: {
                    icon: 'M4.1,28.9h7.1l9.3-22l7.4,38l9.7-19.7l3,12.8h14.9M4.1,58h51.4',
                    show: true,
                    title: 'Line',
                    onclick: () => {
                        dispatch(actions.showLine())
                    },
                },
                myBar: {
                    icon: 'M6.7,22.9h10V48h-10V22.9zM24.9,13h10v35h-10V13zM43.2,2h10v46h-10V2zM3.1,58h53.7',
                    show: true,
                    title: 'Bar',
                    onclick: () => {
                        dispatch(actions.showBar())
                    },
                },
                dataView: { show: true, readOnly: true, title: 'Table', lang: ['Table', 'Close', 'Refresh'] },
                saveAsImage: { show: true, title: 'Save' },
                myDollars: {
                    icon: 'M1362 1185q0 153-99.5 263.5t-258.5 136.5v175q0 14-9 23t-23 9h-135q-13 0-22.5-9.5t-9.5-22.5v-175q-66-9-127.5-31t-101.5-44.5-74-48-46.5-37.5-17.5-18q-17-21-2-41l103-135q7-10 23-12 15-2 24 9l2 2q113 99 243 125 37 8 74 8 81 0 142.5-43t61.5-122q0-28-15-53t-33.5-42-58.5-37.5-66-32-80-32.5q-39-16-61.5-25t-61.5-26.5-62.5-31-56.5-35.5-53.5-42.5-43.5-49-35.5-58-21-66.5-8.5-78q0-138 98-242t255-134v-180q0-13 9.5-22.5t22.5-9.5h135q14 0 23 9t9 23v176q57 6 110.5 23t87 33.5 63.5 37.5 39 29 15 14q17 18 5 38l-81 146q-8 15-23 16-14 3-27-7-3-3-14.5-12t-39-26.5-58.5-32-74.5-26-85.5-11.5q-95 0-155 43t-60 111q0 26 8.5 48t29.5 41.5 39.5 33 56 31 60.5 27 70 27.5q53 20 81 31.5t76 35 75.5 42.5 62 50 53 63.5 31.5 76.5 13 94z',
                    show: true,
                    title: 'Dollars',
                    onclick: () => {
                        dispatch(actions.showDollars())
                    },
                },
                myDonors: {
                    icon: 'M224.274,240.756c-41.43-8.485-72.646-45.149-72.646-89.127c0-33.302,18.094-62.1,44.778-77.959c8.499,18.316,25.856,37.031,49.753,50.848c31.332,18.078,64.558,22.535,86.119,13.903c0.653,4.354,1.307,8.679,1.307,13.208c0,43.978-31.162,80.643-72.619,89.127c58.222-8.9,102.941-58.74,102.941-119.452C363.908,54.315,309.598,0,242.606,0c-66.961,0-121.302,54.315-121.302,121.304C121.304,182.016,166.08,231.855,224.274,240.756z M333.586,272.93h-15.163c0,41.878-33.943,75.817-75.816,75.817c-41.875,0-75.812-33.939-75.812-75.817h-15.166c-50.227,0-90.978,40.726-90.978,90.976v90.98c0,16.764,13.565,30.326,30.327,30.326h303.258c16.759,0,30.326-13.562,30.326-30.326v-90.98C424.562,313.656,383.869,272.93,333.586,272.93z',
                    show: true,
                    title: 'Donors',
                    onclick: () => {
                        dispatch(actions.showDonors())
                    },
                },
            },
        },
        legend: {
            ...stateToProps.options.legend,
            type: 'scroll',
            width: '75%',
        },
    },
})

export const Dashboard = connect(mapStateToProps, null as any, mergeProps)(DashboardVisualization) as any
